// Multi-shop locations settings: add/rename/deactivate/delete locations plus
// a per-location custom-hours override. Mirrors BaysSettings.tsx's inline
// click-to-edit + toast pattern for the list, and reimplements a minimal
// weekly hours grid modeled on ShopHoursSettings.tsx (not refactored/shared).
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  DEFAULT_OPERATING_HOURS, WEEKDAYS, type Location, type OperatingHours, type Weekday,
} from '@wrapforce/shared';
import { apiErrorMessage } from '@/lib/api-errors';
import { useLocaleFormatters } from '@/lib/format';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { useCreateLocation, useDeleteLocation, useLocations, useUpdateLocation } from './api';

const REFERENCE_MONDAY = new Date(Date.UTC(2024, 0, 1));
const OPEN_OPTIONS = Array.from({ length: 48 }, (_, i) => i * 30);
const CLOSE_OPTIONS = Array.from({ length: 48 }, (_, i) => (i + 1) * 30);

export function ShopsSection({ workspaceId, isOwner }: { workspaceId: string | null; isOwner: boolean }) {
  const { t } = useTranslation('locations');
  const { data, isLoading } = useLocations(workspaceId);
  const createLocation = useCreateLocation(workspaceId);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [editing, setEditing] = useState<Location | null>(null);

  const locations = [...(data?.items ?? [])].sort((a, b) => a.sort - b.sort);

  async function handleAdd() {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createLocation.mutateAsync({ name: trimmed, address: address.trim() });
      setName('');
      setAddress('');
      toast.success(t('addedToast'));
    } catch (err) {
      toast.error(t('saveError'), { description: apiErrorMessage(err) });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          {locations.map((loc) => (
            <LocationRow key={loc.id} location={loc} workspaceId={workspaceId} isOwner={isOwner} onEdit={() => setEditing(loc)} />
          ))}
          {!isLoading && locations.length === 0 ? <p className="text-sm text-muted">{t('empty')}</p> : null}
        </div>

        <div className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center">
          <Input
            placeholder={t('addNamePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="max-w-xs"
          />
          <Input
            placeholder={t('addAddressPlaceholder')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="max-w-xs"
          />
          <Button size="sm" variant="secondary" className="w-fit gap-1.5" disabled={!name.trim()} loading={createLocation.isPending} onClick={handleAdd}>
            <Plus size={13} /> {t('add')}
          </Button>
        </div>
      </CardContent>

      {editing ? <EditLocationDialog location={editing} workspaceId={workspaceId} onClose={() => setEditing(null)} /> : null}
    </Card>
  );
}

function LocationRow({
  location, workspaceId, isOwner, onEdit,
}: {
  location: Location;
  workspaceId: string | null;
  isOwner: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation('locations');
  const updateLocation = useUpdateLocation(workspaceId);
  const deleteLocation = useDeleteLocation(workspaceId);
  const { confirm, ConfirmDialog } = useConfirm();

  async function handleToggleActive() {
    try {
      await updateLocation.mutateAsync({ locationId: location.id, input: { isActive: !location.isActive } });
      toast.success(location.isActive ? t('deactivatedToast') : t('reactivatedToast'));
    } catch (err) {
      toast.error(t('saveError'), { description: apiErrorMessage(err) });
    }
  }

  async function handleDelete() {
    const ok = await confirm({
      title: t('deleteConfirmTitle', { name: location.name }),
      description: t('deleteConfirmDescription'),
      confirmLabel: t('common:actions.delete'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteLocation.mutateAsync(location.id);
      toast.success(t('deletedToast'));
    } catch (err) {
      toast.error(t('deleteError'), { description: apiErrorMessage(err) });
    }
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2 border border-border px-3 py-2', !location.isActive ? 'opacity-60' : '')}>
      <ConfirmDialog />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-ink">{location.name}</div>
        <div className="truncate text-xs text-muted">{location.address || t('noAddress')}</div>
      </div>
      <span
        className={cn(
          'px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide',
          location.isActive ? 'bg-success/15 text-success' : 'bg-surface-2 text-muted',
        )}
      >
        {location.isActive ? t('active') : t('inactive')}
      </span>
      {location.operatingHours !== null ? (
        <span className="bg-accent/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-accent-ink">
          {t('customHoursBadge')}
        </span>
      ) : null}
      <Button size="sm" variant="outline" onClick={onEdit}>
        {t('common:actions.edit')}
      </Button>
      <Button size="sm" variant="outline" onClick={handleToggleActive}>
        {location.isActive ? t('deactivate') : t('reactivate')}
      </Button>
      {isOwner ? (
        <Button size="sm" variant="ghost" className="text-danger hover:text-danger" onClick={handleDelete}>
          {t('common:actions.delete')}
        </Button>
      ) : null}
    </div>
  );
}

function EditLocationDialog({
  location, workspaceId, onClose,
}: {
  location: Location;
  workspaceId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('locations');
  const { formatDate, formatTimeOfDay } = useLocaleFormatters();
  const updateLocation = useUpdateLocation(workspaceId);
  const [name, setName] = useState(location.name);
  const [address, setAddress] = useState(location.address);
  const [customHours, setCustomHours] = useState(location.operatingHours !== null);
  const [hours, setHours] = useState<OperatingHours>(location.operatingHours ?? DEFAULT_OPERATING_HOURS);

  function dayName(index: number) {
    const date = new Date(REFERENCE_MONDAY);
    date.setUTCDate(date.getUTCDate() + index);
    return formatDate(date, 'EEEE');
  }

  function setDay(day: Weekday, value: OperatingHours[Weekday]) {
    setHours((prev) => ({ ...prev, [day]: value }));
  }

  function toggleClosed(day: Weekday, closed: boolean) {
    setDay(day, closed ? null : { open: 480, close: 1080 });
  }

  const errors: Partial<Record<Weekday, string>> = {};
  if (customHours) {
    for (const day of WEEKDAYS) {
      const value = hours[day];
      if (value && value.open >= value.close) errors[day] = t('invalidRange');
    }
  }
  const hasErrors = Object.keys(errors).length > 0;
  const trimmedName = name.trim();

  async function handleSave() {
    if (!trimmedName) return;
    try {
      await updateLocation.mutateAsync({
        locationId: location.id,
        input: {
          name: trimmedName,
          address: address.trim(),
          operatingHours: customHours ? hours : null,
        },
      });
      toast.success(t('saved'));
      onClose();
    } catch (err) {
      toast.error(t('saveError'), { description: apiErrorMessage(err) });
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} title={t('editTitle')} description={t('editDescription')}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="location-name">{t('nameLabel')}</Label>
          <Input id="location-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="location-address">{t('addressLabel')}</Label>
          <Input id="location-address" value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>

        <label className="flex cursor-pointer items-center justify-between gap-4 border-t border-border pt-3">
          <span className="text-sm font-semibold text-ink">{t('customHoursToggle')}</span>
          <input
            type="checkbox"
            checked={customHours}
            onChange={(e) => setCustomHours(e.target.checked)}
            className="h-4 w-4 accent-[var(--color-accent)]"
          />
        </label>

        {customHours ? (
          <div className="flex flex-col gap-3">
            {WEEKDAYS.map((day, index) => {
              const value = hours[day];
              const closed = value === null;
              return (
                <div key={day} className="flex flex-col gap-1.5 border-b border-border pb-3 last:border-b-0 last:pb-0">
                  <div className="flex items-center gap-4">
                    <span className="w-24 flex-none text-sm font-semibold text-ink">{dayName(index)}</span>
                    <label className="flex flex-none cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={closed}
                        onChange={(e) => toggleClosed(day, e.target.checked)}
                        className="h-4 w-4 accent-[var(--color-accent)]"
                      />
                      <span className="text-sm text-muted">{t('closed')}</span>
                    </label>
                    {!closed && value ? (
                      <div className="flex flex-1 items-center gap-2">
                        <select
                          value={value.open}
                          onChange={(e) => setDay(day, { ...value, open: Number(e.target.value) })}
                          className="h-9 border border-border bg-surface px-2 text-sm"
                        >
                          {OPEN_OPTIONS.map((m) => (
                            <option key={m} value={m}>{formatTimeOfDay(m)}</option>
                          ))}
                        </select>
                        <span className="text-sm text-muted">{t('to')}</span>
                        <select
                          value={value.close}
                          onChange={(e) => setDay(day, { ...value, close: Number(e.target.value) })}
                          className="h-9 border border-border bg-surface px-2 text-sm"
                        >
                          {CLOSE_OPTIONS.map((m) => (
                            <option key={m} value={m}>{formatTimeOfDay(m)}</option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                  </div>
                  <FieldError>{errors[day]}</FieldError>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-muted">{t('inheritsWorkspaceHours')}</p>
        )}

        <Button onClick={handleSave} loading={updateLocation.isPending} disabled={hasErrors || !trimmedName} className="w-fit">
          {t('common:actions.save')}
        </Button>
      </div>
    </Dialog>
  );
}
